// Etiquetas de entidad: "Cuartel" + "Cuartel 3" no debe dar "Cuartel Cuartel 3".
//
// Varias pantallas anteponen el TIPO de entidad al nombre del registro para que
// el título se lea solo ("Cuartel 301", "Estación Norte"). Cuando el usuario ya
// bautizó el registro incluyendo el tipo — que es lo natural al nombrarlo desde
// Administración — el prefijo queda duplicado y se ve en el menú lateral y en
// el encabezado del detalle.
//
// La corrección es de PRESENTACIÓN, genérica y sin casos especiales: el nombre
// guardado no se toca nunca. `cuarteles.nombre` sigue siendo "Cuartel 3"; lo
// único que cambia es que no se le antepone algo que ya dice.
//
// Espejo en la API: helpers/menu/entityMenu.js (etiquetaEntidad), que arma el
// nombre del ítem de menú. Si esta regla cambia, cambia en ambos: son dos
// runtimes distintos y no pueden compartir el módulo.

// Compara ignorando mayúsculas y tildes: "estacion" y "Estación" son la misma
// palabra para efectos de no repetirla.
const plegar = (valor) => String(valor ?? '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase();

const escaparRegExp = (valor) => String(valor ?? '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// ¿El nombre ya contiene el tipo como PALABRA COMPLETA?
//
// Palabra completa y no subcadena: "Cuartelillo" no contiene "Cuartel" para
// esto, y sí lo contiene "Cuartel 3" o "Sector 2 Cuartel Norte". Sin el límite
// de palabra, un nombre como "Estacionamiento" bloquearía el prefijo
// "Estación"... que es justamente la clase de caso especial que no queremos.
export function nombreIncluyeTipo(nombre, tipo) {
  const t = plegar(tipo).trim();
  const n = plegar(nombre).trim();
  if (!t || !n) return false;
  // \b no sirve con acentos ya plegados en algunos motores antiguos, pero acá
  // ambos lados están normalizados a ASCII-ish, así que es seguro.
  return new RegExp(`\\b${escaparRegExp(t)}\\b`).test(n);
}

// Colapsa palabras consecutivas repetidas: "Cuartel Cuartel 3" -> "Cuartel 3".
//
// Complementa a etiquetaEntidad para las etiquetas que YA vienen duplicadas y
// que no generamos en este render: el menú lateral se arma en la API y se
// guarda en `sessionStorage.menu`, así que un menú emitido antes de la
// corrección (o cacheado en la sesión en curso) seguiría mostrando el duplicado
// hasta reiniciar sesión. Aplicarlo al pintar hace que la corrección no dependa
// de reiniciar el backend ni de limpiar el caché.
//
// Genérico y conservador: solo une repeticiones ADYACENTES e idénticas palabra
// a palabra. "Cuartel 3 Cuartel Norte" no se toca (las repetidas no son
// contiguas) y "Villa Villarrica" tampoco (no son la misma palabra). Se
// conserva la ocurrencia posterior, para no alterar el nombre del registro.
export function sinTipoDuplicado(nombre) {
  const limpio = String(nombre ?? '').trim().replace(/\s+/g, ' ');
  if (!limpio) return '';
  const palabras = limpio.split(' ');
  const salida = [];
  for (const palabra of palabras) {
    const previa = salida[salida.length - 1];
    if (previa !== undefined && plegar(previa) === plegar(palabra)) {
      salida[salida.length - 1] = palabra; // conserva la ocurrencia posterior
      continue;
    }
    salida.push(palabra);
  }
  return salida.join(' ');
}

// Etiqueta visible de una entidad. Devuelve el nombre tal cual si ya nombra su
// tipo; si no, lo antepone. Nunca modifica el nombre en sí.
export function etiquetaEntidad(tipo, nombre) {
  const limpio = String(nombre ?? '').trim();
  if (!limpio) return String(tipo ?? '').trim();
  if (!String(tipo ?? '').trim()) return limpio;
  return nombreIncluyeTipo(limpio, tipo) ? limpio : `${String(tipo).trim()} ${limpio}`;
}

export default { etiquetaEntidad, nombreIncluyeTipo, sinTipoDuplicado };
